import React from 'react'
// Direct-module import, not the package barrel — see index.ts's re-export comment for why.
import { useAuthStore } from '../../../../passport/packages/@passport/store/src/authStore'
import type { User } from '../../../../passport/packages/@passport/store/src/authStore'
import { useSsoHandoffSession } from '../hooks/useSsoHandoffSession'
import { LoginRedirectPage } from './LoginRedirectPage'
import type { LoginMethodFlags } from './LoginRedirectPage'

const DEFAULT_LOADING_FALLBACK: React.ReactElement = (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    Loading...
  </div>
)

export interface SsoAuthGateProps {
  /** Shown on the login app's page ("Signing you in to <appName>…") and in its own
   *  "keeps redirecting" loop-detection screen. */
  appName: string
  /** This app's own API base URL — forwarded to `useSsoHandoffSession` for the handoff-
   *  code redemption / profile-fetch calls, and to `LoginRedirectPage` for tenant branding. */
  apiBaseUrl: string
  /** See `UseSsoHandoffSessionOptions.mirrorLegacyAuthTokenKey` — only apps with API
   *  clients that predate the SSO migration and still read a plain localStorage key
   *  directly need this. Omit for a clean migration. */
  mirrorLegacyAuthTokenKey?: string
  /** Which login methods this app wants enabled on the login app's page. Forwarded
   *  straight through to `LoginRedirectPage` — see its own doc comment. */
  methods?: LoginMethodFlags
  /** Shown while the initial session check (existing-session verify, or handoff-code
   *  redemption) is in flight. Defaults to a bare centered "Loading…" — override with
   *  this app's own themed loading screen (e.g. wrapped in `ThemeProvider`/
   *  `TenantBrandingAssets`) if a bare default would look out of place. */
  loadingFallback?: React.ReactNode
  /** Rendered once a real session is confirmed, given the authenticated `User`. A
   *  render-prop (not plain `children`) so apps that pass `user` down as an explicit
   *  prop to their own shell component (e.g. `<StudioShell authUser={user} .../>`) don't
   *  need a second `useAuthStore()` call just to get it again. */
  children: (user: User) => React.ReactNode
}

/**
 * `SsoAuthGate` — the single drop-in replacement for the ~100-150 lines of hand-rolled
 * session-check/redirect-branch boilerplate every migrated app used to carry in its own
 * `App.tsx` (see git history: flow-studio, saas-tenant-administration, form-studio all had
 * near-identical copies before `useSsoHandoffSession` was extracted). This wraps that hook
 * plus the loading/redirect branching every one of those copies also repeated identically:
 *
 * ```tsx
 * <SsoAuthGate appName="My App" apiBaseUrl={API_BASE_URL}>
 *   {(user) => (
 *     <BrowserRouter>
 *       <Routes>
 *         <Route path="/*" element={<AppShell user={user} />} />
 *       </Routes>
 *     </BrowserRouter>
 *   )}
 * </SsoAuthGate>
 * ```
 *
 * Deliberately does NOT own `BrowserRouter`/`ThemeProvider`/`TenantBrandingAssets` — apps
 * differ on router basename, whether they use `@bizfirst/common-react`'s ThemeProvider at
 * all (form-studio has its own separate theme store instead), and where tenant branding
 * should mount — those stay each app's own choice, wrapped around (or inside) this gate.
 *
 * Always requests the exchange-code handoff (`handoffMode="code"` — never the legacy
 * `?token=<raw JWT>` handoff): an app reaching for this component is by definition opting
 * into that migration. Pairs with `<AuthHeaderControls handoffMode="code">` /
 * `<UserMenuButton handoffMode="code">` in this same app's header/shell for the logout
 * side of the same contract — without that half, logout drops the handoff preference and
 * the next sign-in silently falls back to the legacy handoff (see LogoutPage.tsx in the
 * login app and this package's own DevelopmentHistoryLog.md for the incident this fixes).
 */
export function SsoAuthGate({
  appName,
  apiBaseUrl,
  mirrorLegacyAuthTokenKey,
  methods,
  loadingFallback = DEFAULT_LOADING_FALLBACK,
  children,
}: SsoAuthGateProps): React.ReactElement {
  const { user } = useAuthStore()
  const { isCheckingSession } = useSsoHandoffSession({ apiBaseUrl, mirrorLegacyAuthTokenKey })

  if (isCheckingSession) {
    return <>{loadingFallback}</>
  }

  if (!user) {
    return <LoginRedirectPage appName={appName} apiBaseUrl={apiBaseUrl} returnUrl={window.location.href} methods={methods} handoffMode="code" />
  }

  return <>{children(user)}</>
}

export default SsoAuthGate
